// Portado da Central do Perito (`src/data/templates-tarefas.ts`, commit f5c1923) em 03/11, só com os imports trocados (000385).
import type { EtapaNum } from "./pipeline";
import type { Prioridade } from "./tipos";

export interface TemplateTarefa {
  id: string;
  titulo: string;
  descricao: string;
  prioridade: Prioridade;
  prazoDias: number; // dias a partir da criação da tarefa
  checklist: string[];
  /** etapa do pipeline em que a tarefa é criada automaticamente; sem etapa = só manual */
  etapa?: EtapaNum;
  /** usa o prazo para aceitar o encargo identificado no despacho, quando houver */
  usaPrazoAceitacao?: boolean;
}

export const TEMPLATES_TAREFA: TemplateTarefa[] = [
  // 1. Nomeação
  {
    id: "analisar-nomeacao",
    etapa: 1,
    titulo: "Analisar a nomeação e os autos",
    descricao: "Entender o objeto da perícia antes de aceitar o encargo",
    prioridade: "alta",
    prazoDias: 2,
    checklist: [
      "Ler o despacho de nomeação",
      "Verificar impedimento ou suspeição",
      "Identificar o objeto da perícia e os quesitos",
      "Estimar as horas de trabalho",
    ],
  },
  {
    id: "aceitar-nomeacao",
    etapa: 1,
    usaPrazoAceitacao: true,
    titulo: "Protocolar aceite e proposta de honorários",
    descricao: "Petição de aceite do encargo com a proposta de honorários",
    prioridade: "alta",
    prazoDias: 5,
    checklist: [
      "Calcular a proposta (horas estimadas × valor da hora)",
      "Juntar currículo e dados de contato profissional",
      "Redigir a petição de aceite",
      "Protocolar no sistema do tribunal",
      "Salvar o comprovante de protocolo",
    ],
  },
  // 2. Aceitação
  {
    id: "acompanhar-proposta",
    etapa: 2,
    titulo: "Acompanhar a manifestação sobre os honorários",
    descricao: "Prazo das partes para se manifestar sobre a proposta",
    prioridade: "media",
    prazoDias: 10,
    checklist: [
      "Verificar a intimação das partes",
      "Acompanhar eventuais impugnações",
      "Responder à impugnação, se houver",
    ],
  },
  // 3. Honorários arbitrados
  {
    id: "registrar-arbitramento",
    etapa: 3,
    titulo: "Registrar os honorários arbitrados",
    descricao: "Conferir a decisão de arbitramento e cadastrar o valor",
    prioridade: "media",
    prazoDias: 3,
    checklist: [
      "Conferir o valor arbitrado na decisão",
      "Identificar quem deve depositar",
      "Cadastrar o honorário na aba Financeiro",
    ],
  },
  // 4. Depósito judicial
  {
    id: "confirmar-deposito",
    etapa: 4,
    titulo: "Confirmar o depósito e iniciar os trabalhos",
    descricao: "Verificar o depósito e comunicar o início da perícia",
    prioridade: "media",
    prazoDias: 5,
    checklist: [
      "Conferir a guia de depósito nos autos",
      "Atualizar o valor na aba Financeiro",
      "Comunicar às partes a data e o local de início dos trabalhos",
    ],
  },
  // 5. Documentação
  {
    id: "solicitar-documentos",
    etapa: 5,
    titulo: "Solicitar documentos às partes",
    descricao: "Levantar e pedir tudo o que a análise vai exigir",
    prioridade: "alta",
    prazoDias: 7,
    checklist: [
      "Listar os documentos necessários por quesito",
      "Enviar a solicitação aos assistentes técnicos ou advogados",
      "Controlar o que foi entregue",
      "Reiterar o pedido do que faltar",
    ],
  },
  // 6. Análise
  {
    id: "analise-tecnica",
    etapa: 6,
    titulo: "Realizar a análise técnica e os cálculos",
    descricao: "Exame dos documentos e elaboração dos cálculos",
    prioridade: "alta",
    prazoDias: 20,
    checklist: [
      "Organizar os documentos por quesito",
      "Conferir os critérios fixados na decisão",
      "Elaborar as planilhas de cálculo",
      "Guardar os papéis de trabalho",
    ],
  },
  // 7. Laudo
  {
    id: "protocolar-laudo",
    etapa: 7,
    titulo: "Elaborar e protocolar o laudo pericial",
    descricao: "Redação, revisão e protocolo do laudo",
    prioridade: "alta",
    prazoDias: 10,
    checklist: [
      "Redigir o laudo",
      "Responder a todos os quesitos",
      "Revisar cálculos e anexos",
      "Assinar digitalmente",
      "Protocolar e salvar o comprovante",
    ],
  },
  // 8. Manifestações
  {
    id: "responder-quesitos",
    etapa: 8,
    titulo: "Responder manifestações e quesitos complementares",
    descricao: "Esclarecimentos pedidos pelas partes ou pelo juízo",
    prioridade: "alta",
    prazoDias: 15,
    checklist: [
      "Ler as manifestações e os pareceres dos assistentes técnicos",
      "Separar os pontos que exigem esclarecimento",
      "Elaborar os esclarecimentos ou o laudo complementar",
      "Protocolar a resposta",
    ],
  },
  // 9. Liberação
  {
    id: "requerer-liberacao",
    etapa: 9,
    titulo: "Requerer a liberação dos honorários",
    descricao: "Pedido de expedição do alvará ou da transferência",
    prioridade: "media",
    prazoDias: 5,
    checklist: [
      "Peticionar requerendo a liberação",
      "Informar os dados bancários",
      "Acompanhar a decisão",
    ],
  },
  // 10. Levantamento
  {
    id: "registrar-recebimento",
    etapa: 10,
    titulo: "Registrar o recebimento dos honorários",
    descricao: "Baixa financeira e obrigações fiscais do recebimento",
    prioridade: "media",
    prazoDias: 5,
    checklist: [
      "Confirmar o crédito na conta",
      "Registrar o recebimento na aba Financeiro",
      "Lançar no Carnê-Leão, quando aplicável",
      "Emitir recibo ou nota fiscal, se aplicável",
    ],
  },
  // Avulsas (só pelo formulário ou pela página da perícia)
  {
    id: "cobranca-honorarios",
    titulo: "Cobrar honorários pendentes",
    descricao: "Peticionar pelo depósito ou pela liberação em atraso",
    prioridade: "media",
    prazoDias: 7,
    checklist: [
      "Verificar o que está pendente nos autos",
      "Redigir a petição de cobrança",
      "Protocolar e acompanhar",
    ],
  },
];

export function modelosDaEtapa(etapa: EtapaNum): TemplateTarefa[] {
  return TEMPLATES_TAREFA.filter((t) => t.etapa === etapa);
}

export function buscarModelo(id: string): TemplateTarefa | undefined {
  return TEMPLATES_TAREFA.find((t) => t.id === id);
}
